// src/portal-app/tabs/data/scheduleConflicts.js
//
// Overlap detection for shifts. There is NO backend endpoint for this — the
// logic lives in ScheduleScreen.js and is extracted here so the Team tab and
// the Schedule screen cannot drift apart.
//
// Times are 'HH:MM'. A shift whose end is <= its start is treated as crossing
// midnight and extended by 24h, so two overnight shifts sharing a date — e.g.
// 22:00-02:00 and 23:00-03:00 — correctly overlap.
//
// Counter-intuitive case: 22:00-02:00 and 01:00-05:00 on the SAME date do
// NOT overlap. Only the first shift crosses midnight (02:00 <= 22:00, so it
// extends to 26:00); the second never wraps (05:00 > 01:00), so it stays at
// 01:00-05:00 — that morning, before the first shift even starts that
// evening. Extending only the shift(s) that actually cross midnight is
// correct: it is what lets two genuinely overlapping overnight shifts match
// while a same-date early-morning shift and a later evening shift do not.
const toMinutes = (hhmm) => {
    const [h, m] = String(hhmm || '0:0').split(':').map(Number);
    return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

export function timesOverlap(s1, e1, s2, e2) {
    const a1 = toMinutes(s1);
    let b1 = toMinutes(e1);
    const a2 = toMinutes(s2);
    let b2 = toMinutes(e2);
    if (b1 <= a1) b1 += 1440;
    if (b2 <= a2) b2 += 1440;
    return a1 < b2 && a2 < b1;
}

// Returns [{ employeeId, employeeName, date, shifts: [a, b] }] — one entry per
// overlapping PAIR, deduped per employee+date so a triple-booking reports once.
export function findConflicts(shifts) {
    if (!Array.isArray(shifts)) return [];
    const byKey = new Map();
    shifts.forEach((s) => {
        const key = `${s.employee_id}|${s.date}`;
        if (!byKey.has(key)) byKey.set(key, []);
        byKey.get(key).push(s);
    });

    const out = [];
    byKey.forEach((group) => {
        for (let i = 0; i < group.length; i += 1) {
            for (let j = i + 1; j < group.length; j += 1) {
                if (timesOverlap(group[i].start_time, group[i].end_time,
                                 group[j].start_time, group[j].end_time)) {
                    out.push({
                        employeeId: group[i].employee_id,
                        employeeName: group[i].employee?.name || '',
                        date: group[i].date,
                        shifts: [group[i], group[j]],
                    });
                    return;   // one report per employee+date is enough
                }
            }
        }
    });
    return out;
}
